import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daeo3h1tto0ceqqnh150";

const CATEGORY_OPTIONS = [
  "职场黑幕",
  "社会不公",
  "财经内幕",
  "教育问题",
  "消费维权",
  "其他议题",
];

const MATERIAL_OPTIONS = [
  "文字说明",
  "聊天截图",
  "文件/合同",
  "录音片段",
  "照片证据",
  "仅口头线索",
];

export default function Submission() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [checked, setChecked] = useState<string[]>([]);

  function toggleMat(m: string) {
    setChecked((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = new FormData(form).get("website_alt")?.toString().trim() ?? "";
    setFormError("");
    if (honeypot) {
      setStatus("success");
      form.reset();
      setChecked([]);
      return;
    }
    setStatus("loading");
    const fd = new FormData(form);
    if (!checked.length) {
      setStatus("error");
      setFormError("请至少选择一类你掌握的线索材料。");
      return;
    }
    fd.set("material", checked.join("、"));
    fd.delete("website_alt");
    try {
      const resp = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || "";
      if (resp.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setChecked([]);
      } else {
        setStatus("error");
        if (serverMsg) setFormError(serverMsg);
        else setFormError("提交失败，请稍后重试或改用私信渠道。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未完成，请检查后重试。");
    }
  }

  return (
    <section id="form" className="scroll-mt-20 bg-background-100 py-16 md:py-20">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">ANONYMOUS FORM</span>
          <h2 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">匿名线索提交</h2>
          <p className="mt-3 text-[15px] leading-7 text-foreground-700">
            无需注册、无需实名。请尽量描述清楚时间、地点与人物关系，并注明你能够提供的材料类型。
            我们会在 1—3 个工作日内初步筛选并进入核实。
          </p>
          <ul className="mt-5 space-y-3">
            {[
              { i: 'ri-lock-line', t: '全程不采集手机号与邮箱' },
              { i: 'ri-user-unfollow-line', t: '提交后可随时以匿名方式补充' },
              { i: 'ri-shield-line', t: '材料仅用于事实核实与报道' },
            ].map((f) => (
              <li key={f.t} className="flex items-center gap-3 text-sm text-foreground-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-primary-600 ring-1 ring-background-200">
                  <i className={`${f.i} text-base`} aria-hidden="true"></i>
                </span>
                {f.t}
              </li>
            ))}
          </ul>
          <p className="mt-6 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
            内容敏感、文字较多或需要来回沟通？建议改用
            <a href="#channel-dm" className="font-semibold text-secondary-700 underline underline-offset-2">
              私信爆料
            </a>
            渠道，沟通更从容。
          </p>
        </div>

        <form
          data-readdy-form
          onSubmit={onSubmit}
          className="rounded-lg bg-background-50 p-6 ring-1 ring-background-200 md:p-7 lg:col-span-3"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="category" className="text-sm font-medium text-foreground-800">线索所属议题</label>
              <select
                id="category"
                name="category"
                required
                defaultValue=""
                className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="" disabled>请选择议题</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="title" className="text-sm font-medium text-foreground-800">一句话概括线索</label>
              <input
                id="title"
                name="title"
                type="text"
                required
                maxLength={60}
                placeholder="例如：某地工厂长期超时排班"
                className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="detail" className="text-sm font-medium text-foreground-800">详细描述</label>
            <textarea
              id="detail"
              name="detail"
              required
              rows={5}
              maxLength={500}
              placeholder="请说明发生了什么、大致发生的时间地点、涉及的主要对象，以及你掌握的事实依据……（可匿名描述，无需留下个人信息）"
              className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            ></textarea>
          </div>

          <div className="mt-4">
            <span className="text-sm font-medium text-foreground-800">你掌握的线索材料</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {MATERIAL_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMat(m)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    checked.includes(m)
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-foreground-500">为避免泄露，请勿在此上传含个人身份信息的原始文件。</p>
          </div>

          {/* honeypot */}
          <input
            type="text"
            name="website_alt"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            readOnly
            className="anti-spam-field"
          />

          {status === "success" && (
            <div className="mt-5 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700 ring-1 ring-primary-200">
              <i className="ri-checkbox-circle-line mr-1" aria-hidden="true"></i>
              已收到你的匿名线索，感谢你的勇气。我们会在核实后通过合适方式与你保持联系。
            </div>
          )}
          {status === "error" && formError && (
            <div className="mt-5 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
              <i className="ri-error-warning-line mr-1" aria-hidden="true"></i>
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "loading" ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i>
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill text-base" aria-hidden="true"></i>
                匿名提交线索
              </>
            )}
          </button>
          <p className="mt-3 text-center text-xs text-foreground-500">
            提交即表示你已知晓并同意我们的脱敏与核实机制。
          </p>
        </form>
      </div>
    </section>
  );
}